/**
 * Digest Instructions Settings Component
 *
 * Lets users customize AI instructions for morning, evening, and weekly digests.
 * Each section has a textarea with character limit, example prompts, save, and test-send.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Check,
  ChevronDown,
  Lightbulb,
  Send,
  Sun,
  Moon,
  CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getUserSettings,
  updateDigestPreferences,
  triggerDigest,
  RateLimitError,
} from '../api/userSettings';
import type { DigestPreferences } from '../api/userSettings';

const MAX_CHARS = 2000;

type DigestType = 'morning' | 'evening' | 'weekly';

interface DigestTypeConfig {
  key: DigestType;
  label: string;
  description: string;
  icon: typeof Sun;
  examples: Array<{ label: string; text: string }>;
}

const DIGEST_TYPES: DigestTypeConfig[] = [
  {
    key: 'morning',
    label: 'Morning Digest',
    description: 'Sent before your day starts. Helps you focus on what matters most today.',
    icon: Sun,
    examples: [
      { label: 'Work-only focus', text: 'Focus on work tasks only, skip personal errands.' },
      { label: 'Due today + overdue', text: 'Highlight what\'s due today and anything overdue. Flag overdue items clearly.' },
      { label: 'Ultra-brief', text: 'Keep it short -- 2 sentences max. Just the top priority and one heads-up.' },
      { label: 'Meeting prep', text: 'Include any tasks related to today\'s calendar events. Call out prep I should do before meetings.' },
      { label: 'Energy-aware', text: 'Put deep-focus work first when my energy is highest. Save admin tasks for the afternoon.' },
    ],
  },
  {
    key: 'evening',
    label: 'Evening Digest',
    description: 'Sent at the end of your day. Reflects on progress and sets up tomorrow.',
    icon: Moon,
    examples: [
      { label: 'Celebrate wins', text: 'Summarize what I completed today. Celebrate small wins and acknowledge progress.' },
      { label: 'Tomorrow preview', text: 'Include a gentle reminder of tomorrow\'s top priority so I can start the morning with clarity.' },
      { label: 'Stuck items', text: 'Call out tasks that haven\'t moved in 3+ days. Suggest whether to break them down, delegate, or drop them.' },
      { label: 'Work-life balance', text: 'If I completed a lot today, tell me to rest. If I didn\'t finish much, be encouraging, not guilt-trippy.' },
    ],
  },
  {
    key: 'weekly',
    label: 'Weekly Digest',
    description: 'Sent once a week. Provides a big-picture view of your progress and patterns.',
    icon: CalendarDays,
    examples: [
      { label: 'Goal tracking', text: 'Focus on progress toward long-term goals, not just tasks completed. Show percentage toward each goal if possible.' },
      { label: 'Pattern spotting', text: 'Include patterns -- what kept getting pushed back, what I completed quickly, and where I tend to procrastinate.' },
      { label: 'Week ahead', text: 'End with a suggested focus for next week based on what\'s overdue and what\'s coming up.' },
      { label: 'Minimal summary', text: 'Just the numbers: tasks completed, tasks added, net change. One sentence on the biggest win and biggest blocker.' },
    ],
  },
];

interface DigestSectionState {
  instructions: string;
  savedInstructions: string;
  isSaving: boolean;
  isSending: boolean;
  showExamples: boolean;
}

type SectionStates = Record<DigestType, DigestSectionState>;

function createInitialState(prefs: DigestPreferences): SectionStates {
  const result = {} as SectionStates;
  for (const config of DIGEST_TYPES) {
    const val = prefs[config.key]?.instructions ?? '';
    result[config.key] = {
      instructions: val,
      savedInstructions: val,
      isSaving: false,
      isSending: false,
      showExamples: false,
    };
  }
  return result;
}

/** Render a single digest type's instruction section (for embedding in grouped layouts). */
export function DigestInstructionSection({ digestType }: { digestType: DigestType }) {
  const config = DIGEST_TYPES.find((c) => c.key === digestType)!;
  const [section, setSection] = useState<DigestSectionState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getUserSettings()
      .then((s) => {
        const val = s.digestPreferences[digestType]?.instructions ?? '';
        setSection({ instructions: val, savedInstructions: val, isSaving: false, isSending: false, showExamples: false });
      })
      .catch(() => toast.error(`Failed to load ${digestType} digest preferences`))
      .finally(() => setIsLoading(false));
  }, [digestType]);

  const handleSave = async () => {
    if (!section) return;
    setSection((s) => s && { ...s, isSaving: true });
    try {
      await updateDigestPreferences({ digestPreferences: { [digestType]: { instructions: section.instructions || null } } });
      setSection((s) => s && { ...s, isSaving: false, savedInstructions: section.instructions });
      toast.success(`${digestType.charAt(0).toUpperCase() + digestType.slice(1)} digest instructions saved`);
    } catch {
      setSection((s) => s && { ...s, isSaving: false });
      toast.error(`Failed to save ${digestType} digest instructions`);
    }
  };

  const handleSendTest = async () => {
    setSection((s) => s && { ...s, isSending: true });
    try {
      await triggerDigest(digestType);
      setSection((s) => s && { ...s, isSending: false });
      toast.success(`Test ${digestType} digest sent! Check your email.`);
    } catch (err) {
      setSection((s) => s && { ...s, isSending: false });
      if (err instanceof RateLimitError) toast.error(err.message);
      else toast.error(`Failed to send test ${digestType} digest`);
    }
  };

  if (isLoading || !section) {
    return <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary-500" /></div>;
  }

  const isDirty = section.instructions !== section.savedInstructions;
  const charCount = section.instructions.length;

  return (
    <div className="border-t border-border-subtle-light dark:border-border-subtle-dark pt-4 mt-4">
      <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-2">
        Custom AI instructions
      </p>
      <textarea
        value={section.instructions}
        onChange={(e) => { if (e.target.value.length <= MAX_CHARS) setSection((s) => s && { ...s, instructions: e.target.value }); }}
        placeholder={`e.g. "${config.examples[0].text}"`}
        rows={2}
        className="w-full px-3 py-2 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[3rem]"
      />
      <div className="flex items-center justify-between mt-1.5">
        <button type="button" onClick={() => setSection((s) => s && { ...s, showExamples: !s.showExamples })} className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
          <Lightbulb className="w-3.5 h-3.5" /><span>Examples</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${section.showExamples ? 'rotate-180' : ''}`} />
        </button>
        <span className={`text-xs tabular-nums ${charCount > MAX_CHARS * 0.9 ? 'text-amber-600 dark:text-amber-400' : 'text-text-tertiary-light dark:text-text-tertiary-dark'}`}>{charCount}/{MAX_CHARS}</span>
      </div>
      <div className={`overflow-hidden transition-all duration-200 ease-in-out ${section.showExamples ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
        <div className="bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-3 space-y-2">
          {config.examples.map((example) => (
            <div key={example.label} className="flex items-start justify-between gap-3 py-1">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-text-primary-light dark:text-text-primary-dark">{example.label}</p>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">{example.text}</p>
              </div>
              <button type="button" onClick={() => setSection((s) => s && { ...s, instructions: example.text })} className="shrink-0 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors">Use this</button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button type="button" onClick={handleSendTest} disabled={isDirty || section.isSending} title={isDirty ? 'Save changes before sending a test' : `Send a test ${digestType} digest`} className="px-3 py-1.5 text-xs font-medium border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
          {section.isSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {section.isSending ? 'Sending...' : 'Send test'}
        </button>
        <button type="button" onClick={handleSave} disabled={!isDirty || section.isSaving} className="px-3 py-1.5 text-xs font-medium bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
          {section.isSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving...</> : <><Check className="w-3.5 h-3.5" />Save</>}
        </button>
      </div>
    </div>
  );
}

export function DigestSettings() {
  const [sections, setSections] = useState<SectionStates | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getUserSettings()
      .then((s) => {
        setSections(createInitialState(s.digestPreferences));
      })
      .catch(() => {
        toast.error('Failed to load digest preferences');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const updateSection = useCallback(
    (key: DigestType, patch: Partial<DigestSectionState>) => {
      setSections((prev) => {
        if (!prev) return prev;
        return { ...prev, [key]: { ...prev[key], ...patch } };
      });
    },
    [],
  );

  const handleSave = useCallback(
    async (key: DigestType) => {
      if (!sections) return;
      const section = sections[key];
      updateSection(key, { isSaving: true });

      try {
        await updateDigestPreferences({
          digestPreferences: {
            [key]: { instructions: section.instructions || null },
          },
        });
        updateSection(key, {
          isSaving: false,
          savedInstructions: section.instructions,
        });
        toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} digest instructions saved`);
      } catch {
        updateSection(key, { isSaving: false });
        toast.error(`Failed to save ${key} digest instructions`);
      }
    },
    [sections, updateSection],
  );

  const handleSendTest = useCallback(
    async (key: DigestType) => {
      updateSection(key, { isSending: true });

      try {
        await triggerDigest(key);
        updateSection(key, { isSending: false });
        toast.success(`Test ${key} digest sent! Check your email.`);
      } catch (err) {
        updateSection(key, { isSending: false });
        if (err instanceof RateLimitError) {
          toast.error(err.message);
        } else {
          toast.error(`Failed to send test ${key} digest`);
        }
      }
    },
    [updateSection],
  );

  if (isLoading || !sections) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
          Digest Instructions
        </h2>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
          Customize what Danny includes in each digest email. Leave blank for the default summary.
        </p>
      </div>

      <div className="space-y-6">
        {DIGEST_TYPES.map((config) => {
          const section = sections[config.key];
          const isDirty = section.instructions !== section.savedInstructions;
          const charCount = section.instructions.length;
          const Icon = config.icon;

          return (
            <div
              key={config.key}
              className="border-t border-border-subtle-light dark:border-border-subtle-dark pt-5 first:border-t-0 first:pt-0"
            >
              {/* Section header */}
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <h3 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {config.label}
                </h3>
              </div>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-3">
                {config.description}
              </p>

              {/* Textarea */}
              <textarea
                value={section.instructions}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= MAX_CHARS) {
                    updateSection(config.key, { instructions: val });
                  }
                }}
                placeholder={`e.g. "${config.examples[0].text}"`}
                rows={3}
                className="w-full px-3 py-2 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[4.5rem]"
              />

              {/* Character counter */}
              <div className="flex items-center justify-between mt-1.5">
                <button
                  type="button"
                  onClick={() =>
                    updateSection(config.key, {
                      showExamples: !section.showExamples,
                    })
                  }
                  className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  <Lightbulb className="w-3.5 h-3.5" />
                  <span>Show examples</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      section.showExamples ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <span
                  className={`text-xs tabular-nums ${
                    charCount > MAX_CHARS * 0.9
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-text-tertiary-light dark:text-text-tertiary-dark'
                  }`}
                >
                  {charCount}/{MAX_CHARS}
                </span>
              </div>

              {/* Examples panel */}
              <div
                className={`overflow-hidden transition-all duration-200 ease-in-out ${
                  section.showExamples ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Example instructions:
                  </p>
                  {config.examples.map((example) => (
                    <div
                      key={example.label}
                      className="flex items-start justify-between gap-3 py-1.5"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-text-primary-light dark:text-text-primary-dark">
                          {example.label}
                        </p>
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                          {example.text}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          updateSection(config.key, {
                            instructions: example.text,
                          })
                        }
                        className="shrink-0 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                      >
                        Use this
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => handleSendTest(config.key)}
                  disabled={isDirty || section.isSending}
                  title={isDirty ? 'Save changes before sending a test' : `Send a test ${config.key} digest`}
                  className="px-3 py-1.5 text-xs font-medium border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {section.isSending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  {section.isSending ? 'Sending...' : 'Send test'}
                </button>
                <button
                  type="button"
                  onClick={() => handleSave(config.key)}
                  disabled={!isDirty || section.isSaving}
                  className="px-3 py-1.5 text-xs font-medium bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {section.isSaving ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
